import { useCallback, useEffect, useState } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

type Row = { id: string; ci_id: string; country: string; career_title: string | null; grade: string; status: string; error: string | null; fetched_at: string | null };

export function RoadmapResearchPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [estimate, setEstimate] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("career_roadmaps")
      .select("id,ci_id,country,career_title,grade,status,error,fetched_at")
      .order("updated_at", { ascending: false })
      .limit(200);
    setRows((data ?? []) as Row[]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (dry: boolean) => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("build-career-roadmap", { body: { batch: 5, dry_run: dry } });
    setBusy(false);
    if (error) {
      const d = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
      toast({ title: "Roadmap run failed", description: d.slice(0, 200), variant: "destructive" });
      return;
    }
    if (dry) setEstimate(`${data.roles} roles × ${data.countries.length} countries: ${data.firecrawl_searches} web searches and ${data.ai_calls} AI calls.`);
    else if (data?.stopped === "firecrawl_credits_exhausted") {
      toast({ title: "Web research paused", description: "The Firecrawl account is out of credits. Add credits or upgrade the plan, then run the batch again.", variant: "destructive" });
      load();
    } else if (data?.stopped === "firecrawl_rate_limited") {
      toast({ title: "Web research paused", description: "Firecrawl is limiting requests right now. Try again in a few minutes.", variant: "destructive" });
      load();
    }
    else { toast({ title: "Batch finished", description: `${data.results?.length ?? 0} roadmaps processed.` }); load(); }
  };

  const setStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("career_roadmaps").update({ status }).eq("id", id);
    if (error) toast({ title: "Couldn't update", description: error.message, variant: "destructive" });
    else load();
  };

  const count = (s: string) => rows.filter((r) => r.status === s).length;

  return (
    <Card>
      <CardHeader><CardTitle>Country roadmaps (US / India)</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Published {count("published")} · Awaiting review {count("review")} · Failed {count("failed")}. Batches run daily, most-saved roles first.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy} onClick={() => run(true)}>Estimate next batch</Button>
          <Button disabled={busy} onClick={() => run(false)}>{busy ? "Running…" : "Run next batch"}</Button>
        </div>
        {estimate && <p className="text-sm">{estimate}</p>}
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <a href={`/careers/${r.ci_id}`} className="min-w-[12rem] flex-1 hover:underline">{r.career_title ?? r.ci_id}</a>
              <span>{r.country}</span>
              <span className="text-muted-foreground">{r.status} · {r.grade}</span>
              {r.error && <span className="text-xs text-destructive">{r.error.slice(0, 80)}</span>}
              {r.status === "review" && <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "published")}>Approve</Button>}
              {r.status === "published" && <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, "rejected")}>Reject</Button>}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
